// ============================================
// User Types
// ============================================
export enum UserType {
    SalesRep = 'salesrep',
    // Backward-compatible alias for older references.
    Sale = 'salesrep',
    Customer = 'customer'
}

export enum UserStatus {
    Online = 'online',
    Offline = 'offline'
}

export interface UserDto {
    userID: string;
    nickname?: string;
    faceURL?: string;
    email?: string;
    phone?: string;
    gender?: number;
    type?: UserType;
    status?: UserStatus;
    ex?: string;
}

// ============================================
// Message Types
// ============================================

// Message sending status
export enum MessageStatus {
    Sending = 1,
    Succeeded = 2,
    Failed = 3,
}

export enum GroupMessageType {
    Normal = 'Normal',
    FirstInGroup = 'FirstInGroup'
}

export interface TextElem {
    content: string;
}

export interface PictureInfo {
    url: string;
    uuid?: string;
    type?: string;
    width?: number;
    height?: number;
    size?: number;
}

export interface PictureElem {
    sourcePicture: PictureInfo;
    bigPicture?: PictureInfo;
    snapshotPicture?: PictureInfo;
}

export interface VideoElem {
    videoUrl: string;
    videoUUID?: string;
    videoType?: string;
    videoSize?: number;
    duration?: number;
    snapshotUrl?: string;
    snapshotUUID?: string;
    snapshotSize?: number;
    snapshotWidth?: number;
    snapshotHeight?: number;
}

export interface FileElem {
    fileName: string;
    filePath?: string;
    uuid?: string;
    sourceUrl: string;
    fileSize?: number;
}

export interface SoundElem {
    uuid?: string;
    soundPath?: string;
    sourceUrl: string;
    dataSize?: number;
    duration: number;
}

export interface LocationElem {
    description: string;
    longitude: number;
    latitude: number;
}

export interface QuoteElem {
    text: string;
    quoteMessage: MessageDto;
}

export interface AtUsersInfoItem {
    atUserID: string;
    groupNickname: string;
}

export interface AtTextElem {
    text: string;
    atUserList: string[];
    atUsersInfo?: AtUsersInfoItem[];
    quoteMessage?: MessageDto;
    isAtSelf?: boolean;
}

export interface MessageDto {
    clientMsgID: string;
    serverMsgID?: string;
    createTime: number;
    sendTime: number;
    sessionType?: number;
    sendID: string;
    recvID?: string;
    msgFrom?: number;
    contentType: number;
    platformID?: number;
    senderNickname?: string;
    senderFaceUrl?: string;
    groupID?: string;
    content?: string;
    seq?: number;
    isRead?: boolean;
    status?: number;
    offlinePush?: any;
    attachedInfo?: string;
    attachedInfoElem?: {
        groupHasReadInfo?: {
            hasReadCount?: number;
            unreadCount?: number;
            hasReadUserIDList?: string[];
            groupMemberCount?: number;
        };
        hasReadTime?: number;
    };
    ex?: string;
    localEx?: string;
    sendError?: string;
    textElem?: TextElem;
    atTextElem?: AtTextElem;
    pictureElem?: PictureElem;
    videoElem?: VideoElem;
    fileElem?: FileElem;
    soundElem?: SoundElem;
    locationElem?: LocationElem;
    quoteElem?: QuoteElem;
    albumElem?: AlbumElem;
    fileName?: string;
    customElem?: {
        data?: string;
        description?: string;
        extension?: string;
    };
    readAt?: number;
}

export interface GroupMessage {
    items: MessageDto[];
    sendID: string;
    sendTime: number;
    isRead?: boolean;
    readAt?: number;
    type?: GroupMessageType;
    albumID?: string;
    albumImages?: MessageDto[];
}

// ============================================
// Room/Group Types
// ============================================
export interface GroupMemberDto {
    userID: string;
    groupID?: string;
    nickname?: string;
    faceURL?: string;
    roleLevel?: number;
    joinTime?: number;
    joinSource?: number;
    inviterUserID?: string;
    operatorUserID?: string;
    muteEndTime?: number;
    ex?: string;
    email?: string;
    phone?: string;
    type?: string;
}

export interface RoomDto {
    groupID: string;        // OpenIM groupID = customerId (e.g. "000635")
    groupName: string;
    customerNo?: string;    // Customer number (e.g. "14-0000") for display only
    notification?: string;
    introduction?: string; // 'open' or 'close' for room status
    faceURL?: string;
    createTime: number;
    ex?: string;
    ownerUserID?: string;
    creatorUserID?: string;
    memberCount?: number;
    status?: number;
    needVerification?: number;
    lookMemberInfo?: number;
    applyMemberFriend?: number;
    notificationUpdateTime?: number;
    notificationUserID?: string;
    // Custom extensions
    symbol?: string;
    bgColor?: string;
    members?: GroupMemberDto[];
    conversation?: ConversationDto;
    lastMessage?: MessageDto;
    emails?: string;
    conversationID?: string;
    typing?: boolean;
    typingUserID?: string;
}

// ============================================
// Conversation Types
// ============================================
export interface ConversationDto {
    conversationID: string;
    conversationType: number;
    userID?: string;
    groupID?: string;
    showName?: string;
    faceURL?: string;
    recvMsgOpt?: number;
    unreadCount: number;
    groupAtType?: number;
    latestMsg?: string | MessageDto;
    latestMsgSendTime?: number;
    draftText?: string;
    draftTextTime?: number;
    isPinned?: boolean;
    isNotInGroup?: boolean;
    isPrivateChat?: boolean;
    attachedInfo?: string;
    ex?: string;
    // Custom extensions
    typing?: boolean;
    typingUserID?: string;
    introduction?: string;
    members?: GroupMemberDto[];
    emails?: string;
    groupInfo?: any;
}

// ============================================
// UI State Types
// ============================================
export enum LayoutState {
    Icon = 'icon',
    Minibar = 'minibar',
    Minimize = 'minimize',
    Maximize = 'maximize',
    Close = 'close'
}

export enum PositionState {
    Widget = 'widget',
    Top = 'top'
}

// ============================================
// API Types
// ============================================
export interface ApiResult<T> {
    errCode: number;
    errMsg?: string;
    data: T;
}

export interface TokenData {
    token: string;
    expireTimeSeconds: number;
}

// ============================================
// Push Notification Types
// ============================================
export interface PushMessage {
    title: string;
    desc: string;
    ex?: string;
    iOSPushSound?: string;
    iOSBadgeCount?: boolean;
}

// ============================================
// Image Processing Types
// ============================================
export interface ImageData {
    source: {
        file: File;
        url: string;
        uuid?: string;
        width: number;
        height: number;
        size: number;
        type: string;
    };
    big?: {
        file: File;
        url: string;
        uuid?: string;
        width: number;
        height: number;
        size: number;
        type: string;
    };
    thumbnail?: {
        file: File;
        url: string;
        uuid?: string;
        width: number;
        height: number;
        size: number;
        type: string;
    };
}

// ============================================
// Message Type Constants (from OpenIM SDK)
// ============================================
export const MessageType = {
    TextMessage: 101,
    PictureMessage: 102,
    VoiceMessage: 103,
    VideoMessage: 104,
    FileMessage: 105,
    AtTextMessage: 106,
    MergeMessage: 107,
    CardMessage: 108,
    LocationMessage: 109,
    CustomMessage: 110,
    RevokeMessage: 111, // Local app revoke display type
    SDK_RevokeMessage: 2101, // OpenIM SDK revoke notification type (received from server)
    HasReadReceiptMessage: 112,
    TypingMessage: 113,
    QuoteMessage: 114,
    FaceMessage: 115,
    AdvancedTextMessage: 117,
    AdvancedRevokeMessage: 118,
    CustomMsgNotTriggerConversation: 119,
    CustomMsgOnlineOnly: 120,
    // Group notifications
    GroupCreatedNotification: 1501,
    GroupInfoSetNotification: 1502,
    JoinGroupApplicationNotification: 1503,
    MemberQuitNotification: 1504,
    GroupApplicationAcceptedNotification: 1505,
    GroupApplicationRejectedNotification: 1506,
    GroupOwnerTransferredNotification: 1507,
    MemberKickedNotification: 1508,
    MemberInvitedNotification: 1509,
    MemberEnterNotification: 1510,
    GroupDismissedNotification: 1511,
    GroupMemberMutedNotification: 1512,
    GroupMemberCancelMutedNotification: 1513,
    GroupMutedNotification: 1514,
    GroupCancelMutedNotification: 1515,
    GroupMemberInfoSetNotification: 1516,
    GroupAnnouncementUpdatedNotification: 1519,
    GroupNameChangedNotification: 1520,
} as const;

// ============================================
// Attachment Types for UI
// ============================================
export interface Attachment {
    id: string;
    url: string;
    name: string;
    size?: number;
    type: 'image' | 'document' | 'video';
}

export interface SelectedFile {
    file: File;
    name: string;
    size: number;
    preview?: string;
    duration?: number;
    thumbnailFile?: File;
    isUploading?: boolean;
    type: 'image' | 'document' | 'video';
}

// ============================================
// Context Menu Types
// ============================================
export interface ContextMenuPosition {
    x: number;
    y: number;
}

// ============================================
// Typing Status Types
// ============================================
export interface TypingStatus {
    conversationID: string;
    userID: string;
    platformIDs?: number[];
}

// ============================================
// Viewer Types
// ============================================
export interface ImageViewerData {
    images: string[];
    currentIndex: number;
}

export interface VideoViewerData {
    urls: string[];
    currentIndex: number;
}

// ============================================
// Reaction Types
// ============================================
export interface MessageReaction {
    emoji: string;
    userIds: string[];
}

export interface MessageReactions {
    [messageId: string]: {
        [emoji: string]: string[];  // emoji -> userIds
    };
}

// ============================================
// Album Types
// ============================================
export interface AlbumElem {
    imageUrls: string[];  // Array of image URLs in the album
    albumId: string;      // Unique album identifier
}

export interface AlbumMetadata {
    optimisticID?: string;  // Client-side temporary ID
    albumID: string;        // Server-side album ID
}

export interface AlbumCache {
    [clientMsgID: string]: string;  // clientMsgID -> albumID mapping
}

// ============================================
// Search Types
// ============================================
export interface SearchResultItem {
    conversationID: string;
    messageCount: number;
    title: string;
    faceURL: string;
    messageList: MessageDto[];
}
